import React, { useState, useEffect } from 'react';
import './MyDiscussions.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

const avatar = '/dashboard/studprof.png';

const INITIAL_DISCUSSIONS = [
  {
    id: 1,
    title: 'Frontend Debugging Help Needed!',
    author: 'Arjun Mehta',
    course: 'Full Stack Web Development',
    snippet: 'I’m stuck with a flexbox layout issue in React. Can someone take a look at my code snippet?',
    unread: true,
    tag: 'General'
  },
  {
    id: 2,
    title: 'Cybersecurity Quiz 3 Clarification',
    author: 'Anjali Verma',
    course: 'Cybersecurity',
    snippet: 'Question 5 seems unclear to me. Has anyone confirmed the right option with the mentor?',
    unread: false,
    tag: 'General'
  },
  {
    id: 3,
    title: 'Final Project: AI Chatbot Suggestions',
    author: 'Arjun Mehta',
    course: 'Python + AI',
    snippet: 'Building a chatbot for my final project. Any suggestions for good NLP tools?',
    unread: false,
    tag: 'Project'
  }
];

export default function MyDiscussions() {
  const [filter, setFilter] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [discussions, setDiscussions] = useState(INITIAL_DISCUSSIONS);
  const [form, setForm] = useState({ title: '', course: '', tag: 'General', snippet: '' });
  const [errors, setErrors] = useState({ title: false, course: false, snippet: false });
  const [showRepliesId, setShowRepliesId] = useState(null);

  useEffect(() => {
    fetch("https://kodez.robokalam.in/api/get-discussions.php")
      .then(res => res.json())
      .then(async (data) => {
        if (Array.isArray(data)) {
          const enriched = await Promise.all(
            data.map(async d => {
              const r = await fetch(`https://kodez.robokalam.in/api/get-replies.php?id=${d.id}`);
              const rdata = await r.json();
              return {
                ...d,
                id: parseInt(d.id),
                unread: true,
                replies: rdata.status === "success" ? rdata.data : []
              };
            })
          );
          setDiscussions([...enriched, ...INITIAL_DISCUSSIONS]);
        }
      })
      .catch(() => alert("⚠️ Failed to load discussions"));
  }, []);


  const shown = discussions.filter(d =>
    filter === 'All' ? true :
      filter === 'Unread' ? d.unread :
        d.tag === filter
  );

  const addDiscussion = () => {
    const newErrors = {
      title: !form.title.trim(),
      course: !form.course.trim(),
      snippet: !form.snippet.trim()
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;

    fetch("https://kodez.robokalam.in/api/save-discussion.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: form.title,
        course: form.course,
        tag: form.tag,
        snippet: form.snippet,
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === "success") {
          // Refresh from DB to fetch with real ID
          return fetch("https://kodez.robokalam.in/api/get-discussions.php");
        } else {
          throw new Error("Failed to save");
        }
      })
      .then(res => res.json())
      .then(data => {
        const fetched = data.map(d => ({
          ...d,
          id: parseInt(d.id),
          unread: true
        }));
        setDiscussions([...fetched, ...INITIAL_DISCUSSIONS]);

        setShowForm(false);
        setForm({ title: '', course: '', tag: 'General', snippet: '' });
        setErrors({ title: false, course: false, snippet: false });
      })
      .catch(() => alert("❌ Failed to save or fetch latest discussion"));
  };

  const deleteDiscussion = (id) => {
    // Prevent deletion of hardcoded discussions
    if (INITIAL_DISCUSSIONS.some(d => d.id === id)) {
      alert("❌ This discussion cannot be deleted.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this discussion?")) return;

    fetch("https://kodez.robokalam.in/api/delete-discussion.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id })
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === "success") {
          setDiscussions(prev => prev.filter(d => d.id !== id));
        } else {
          alert("Failed to delete discussion.");
        }
      })
      .catch(() => alert("Server error during delete."));
  };

  const handleThreadClick = (id) => {
    setDiscussions(prev =>
      prev.map(d => d.id === id ? { ...d, unread: false } : d)
    );
  };

  return (
    <div className="discussions-wrapper">
      <div className="discussions-header">
        <h1>My Discussions</h1>
        <button className="new-btn" onClick={() => setShowForm(true)}>
          ＋ New Discussion
        </button>
      </div>

      <div className="filter-row">
        {['All', 'Unread', 'General'].map(b => (
          <button
            key={b}
            className={`pill ${filter === b ? 'active' : ''}`}
            onClick={() => setFilter(b)}
          >
            {b}
          </button>
        ))}
      </div>
      <hr />

      <div className="threads">
        {shown.map(d => (
          <div key={d.id} className={`thread-card ${d.unread ? 'unread' : ''}`} onClick={() => handleThreadClick(d.id)}>
            <img src={avatar} alt="avatar" />
            {showRepliesId === d.id && d.replies && d.replies.length > 0 && (
              <div className="popup-replies">
                <h4>Mentor Replies</h4>
                {d.replies.map((r, idx) => (
                  <div key={idx} className="popup-reply">
                    <strong>{r.author}</strong>: {r.message}
                    <div className="reply-time">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                ))}
                <button className="close-popup-btn" onClick={() => setShowRepliesId(null)}>Close</button>
              </div>
            )}

            <div className="thread-info">
              <h3>{d.title}</h3>
              <p className="meta">
                Started by: <strong>{d.author}</strong> • Course: {d.course}
              </p>
              <p className="snippet">“{d.snippet}”</p>
            </div>

            <div className="thread-actions">
              <button
                className="view-reply-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  fetch(`https://kodez.robokalam.in/api/get-replies.php?id=${d.id}`)
                    .then(res => res.json())
                    .then(replyData => {
                      const updated = discussions.map(disc => {
                        if (disc.id === d.id) {
                          return { ...disc, replies: replyData.data || [] };
                        }
                        return disc;
                      });
                      setDiscussions(updated);
                      setShowRepliesId(showRepliesId === d.id ? null : d.id);
                    });
                }}
              >
                View Mentor Reply
              </button>

              <button
                className="delete-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteDiscussion(d.id);
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <p className="empty">No threads in this view 🎉</p>}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-form" onClick={e => e.stopPropagation()}>
            <h2>Start a New Discussion</h2>
            <input
              className={errors.title ? 'input-error' : ''}
              placeholder="Discussion Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <input
              className={errors.course ? 'input-error' : ''}
              placeholder="Course Name"
              value={form.course}
              onChange={(e) => setForm({ ...form, course: e.target.value })}
            />
            <select
              value={form.tag}
              onChange={(e) => setForm({ ...form, tag: e.target.value })}
            >
              <option value="General">General</option>
              <option value="Project">Project</option>
              <option value="Quiz">Quiz</option>
            </select>
            <textarea
              className={errors.snippet ? 'input-error' : ''}
              placeholder="Write your doubt or message..."
              value={form.snippet}
              onChange={(e) => setForm({ ...form, snippet: e.target.value })}
            />
            <button className="post-btn" onClick={addDiscussion}>Post Discussion</button>
          </div>
        </div>
      )}
    </div>
  );
}
